import React from 'react';
import { t } from '../../utils/translations';
import { getFormattedUserName } from '../../utils/cvUtils';

export default function CVTemplateATSCompact({ user, projects, language = 'id' }) {

  // Helper function to categorize education
  const categorizeEducation = (education) => {
    const higher = [];
    const k12 = [];
    
    education.forEach(edu => {
      const degree = edu.degree?.toLowerCase() || '';
      const isHigherEd = degree.includes('sarjana') || degree.includes('s1') || 
                        degree.includes('s2') || degree.includes('s3') || 
                        degree.includes('bachelor') || degree.includes('master') || 
                        degree.includes('magister') || degree.includes('phd') || 
                        degree.includes('doktor') || degree.includes('diploma');
      
      if (isHigherEd) {
        higher.push(edu);
      } else {
        k12.push(edu);
      }
    });
    
    return { higher, k12 };
  };

  const { higher: higherEducation, k12: k12Education } = user?.education ? 
    categorizeEducation(user.education) : { higher: [], k12: [] };

  return (
    <div className="bg-white p-6 max-w-[21cm] mx-auto" style={{ fontFamily: 'Calibri, Arial, sans-serif', lineHeight: '1.3' }}>
      {/* Compact Header */}
      <div className="text-center mb-4 border-b-2 border-black pb-3">
        <h1 className="text-2xl font-bold text-black mb-0.5">{getFormattedUserName(user) || 'YOUR NAME'}</h1>
        <p className="text-sm text-black mb-1 text-center">{user?.job || 'Professional Title'}</p>
        <div className="text-xs text-black">
          {[user?.email, user?.phone, user?.location].filter(Boolean).join(' | ')}
        </div>
      </div>

      {/* Compact Summary */}
      {user?.summary && (
        <div className="mb-3">
          <h2 className="text-xs font-bold text-black uppercase border-b border-black mb-1">
            {t('profile', language)}
          </h2>
          <p className="text-xs text-black leading-snug text-justify">{user.summary}</p>
        </div>
      )}

      {/* Compact Skills */}
      {user?.skills && user.skills.length > 0 && (
        <div className="mb-3">
          <h2 className="text-xs font-bold text-black uppercase border-b border-black mb-1">
            {t('skills', language)}
          </h2>
          <p className="text-xs text-black">
            {user.skills.join(' • ')}
          </p>
        </div>
      )}

      {/* Compact Experience */}
      {user?.experience && user.experience.length > 0 && (
        <div className="mb-3">
          <h2 className="text-xs font-bold text-black uppercase border-b border-black mb-1">
            {t('experience', language)}
          </h2>
          <div className="space-y-2">
            {user.experience.map((exp, i) => (
              <div key={i}>
                <div className="flex justify-between items-baseline">
                  <h3 className="text-xs font-bold text-black">{exp.position}</h3>
                  <span className="text-xs text-black">{exp.period}</span>
                </div>
                <p className="text-xs text-black italic">{exp.company}</p>
                {exp.description && (
                  <p className="text-xs text-black text-justify leading-snug mt-0.5">{exp.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compact Education - Higher Education */}
      {higherEducation.length > 0 && (
        <div className="mb-3">
          <h2 className="text-xs font-bold text-black uppercase border-b border-black mb-1">
            {t('higherEducation', language)}
          </h2>
          <div className="space-y-1.5">
            {higherEducation.map((edu, i) => (
              <div key={i} className="flex justify-between items-baseline">
                <div>
                  <h3 className="text-xs font-bold text-black">{edu.degree}</h3>
                  <p className="text-xs text-black">{edu.school || edu.institution}</p>
                </div>
                <span className="text-xs text-black">{edu.year}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compact Education - K-12 */}
      {k12Education.length > 0 && (
        <div className="mb-3">
          <h2 className="text-xs font-bold text-black uppercase border-b border-black mb-1">
            {t('formalEducation', language)}
          </h2>
          <div className="space-y-1.5">
            {k12Education.map((edu, i) => (
              <div key={i} className="flex justify-between items-baseline">
                <div>
                  <h3 className="text-xs font-bold text-black">{edu.degree}</h3>
                  <p className="text-xs text-black">{edu.school || edu.institution}</p>
                </div>
                <span className="text-xs text-black">{edu.year}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compact Projects */}
      {projects && projects.length > 0 && (
        <div className="mb-3">
          <h2 className="text-xs font-bold text-black uppercase border-b border-black mb-1">
            {t('projects', language)}
          </h2>
          <div className="space-y-1.5">
            {projects.map((proj, i) => (
              <div key={i}>
                <h3 className="text-xs font-bold text-black">{proj.title}</h3>
                <p className="text-xs text-black text-justify leading-snug">{proj.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
